import React, { useEffect, useState } from 'react';
import { useNavigate, Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { checkAuthStatus, logout, getAdminUser } from '../controllers/authController';
import { fetchAdminStats, initAdminStats, fetchRecentActivities, logActivity } from '../controllers/adminStatsController';
import WishlistView from './WishlistView';
import EmailCampaignView from './EmailCampaignView';
import SecurityMonitor from '../components/SecurityMonitor';

const DashboardView = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [showSecurityMonitor, setShowSecurityMonitor] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    wishlistEntries: 0,
    emailsSent: 0,
    conversionRate: 0,
    recentActivity: 0,
    systemHealth: 98.5
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuthenticated = await checkAuthStatus(navigate);
      
      if (isAuthenticated) {
        const adminUser = await getAdminUser();
        setUser(adminUser);
        
        // Load real dashboard stats from Firebase
        await loadDashboardStats();
        
        // Load recent activities
        await loadRecentActivities();
      }
      
      setIsLoading(false);
    };
    
    verifyAuth();
    
    // Set active section based on URL
    const path = window.location.pathname;
    if (path.includes('/wishlist')) {
      setActiveSection('wishlist');
    } else if (path.includes('/email-campaign')) {
      setActiveSection('email-campaign');
    } else if (path.includes('/settings')) {
      setActiveSection('settings');
    } else if (path.includes('/analytics')) {
      setActiveSection('analytics');
    } else {
      setActiveSection('overview');
    }
  }, [navigate]);

  const loadDashboardStats = async () => {
    setStatsLoading(true);
    setStatsError('');
    
    try {
      // Initialize admin stats if they don't exist
      await initAdminStats();
      
      // Fetch real stats from Firebase
      const result = await fetchAdminStats();
      
      if (result.success) {
        setDashboardStats(result.data);
      } else {
        console.error('Failed to load dashboard stats:', result.error);
        setStatsError(result.error || 'Failed to load dashboard statistics');
        // Keep default values if fetch fails
      }
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setStatsError('An error occurred while loading dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    setActivitiesLoading(true);
    
    try {
      const result = await fetchRecentActivities(8); // Limit to 8 activities
      
      if (result.success) {
        setRecentActivities(result.data);
      } else {
        console.error('Failed to load recent activities:', result.error);
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Error loading recent activities:', error);
      setRecentActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout(navigate);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    
    // Navigate to the corresponding route
    if (section === 'overview') {
      navigate('/admin/dashboard');
    } else {
      navigate(`/admin/dashboard/${section}`);
    }
  };

  const refreshDashboardStats = () => {
    loadDashboardStats();
    loadRecentActivities();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-lg font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-zinc-900 to-zinc-800 shadow-2xl border-b border-zinc-700">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div 
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.02 }}
          >
            <div className="relative">
              <img 
                src="/images/memic_logo_3.png" 
                alt="Memic Admin" 
                className="h-12 w-24" 
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <span className="text-2xl font-bold text-white">ADMIN</span>
              <p className="text-xs text-gray-400">Management Console</p>
            </div>
          </motion.div>
          
          <div className="flex items-center space-x-6">
            {/* Quick Stats */}
            <div className="hidden md:flex items-center space-x-4 mr-4">
              <div className="text-center">
                <p className="text-sm text-gray-400">System Health</p>
                <p className="text-green-400 font-bold">{dashboardStats.systemHealth}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-400">Active Users</p>
                <p className="text-blue-400 font-bold">{dashboardStats.recentActivity}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-white font-semibold">
                  {user?.email || 'Admin User'}
                </p>
                <p className="text-green-400 text-sm flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
                  Online
                </p>
              </div>
              
              <motion.button
                onClick={() => setShowSecurityMonitor(!showSecurityMonitor)}
                className="bg-zinc-700 hover:bg-zinc-600 text-white p-3 rounded-xl transition-all duration-200 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Security Monitor"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </motion.button>
              
              <motion.button
                onClick={handleLogout}
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-4 py-2 rounded-xl transition-all duration-200 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </motion.button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Enhanced Sidebar */}
          <div className="lg:col-span-1">
            <motion.div 
              className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-white">Control Panel</h2>
              </div>
              
              <nav className="space-y-2">
                {[
                  { 
                    id: 'overview', 
                    name: 'Dashboard', 
                    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
                    gradient: 'from-blue-500 to-blue-600'
                  },
                  { 
                    id: 'wishlist', 
                    name: 'Wishlist', 
                    icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                    gradient: 'from-pink-500 to-pink-600',
                    count: dashboardStats.wishlistEntries
                  }
                ].map((item) => (
                  <motion.div
                    key={item.id}
                    onClick={() => handleSectionChange(item.id)}
                    className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-white to-gray-100 text-black shadow-lg'
                        : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300 hover:text-white'
                    }`}
                    whileHover={{ x: 4, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                          activeSection === item.id 
                            ? `bg-gradient-to-r ${item.gradient} text-white` 
                            : 'bg-zinc-600 group-hover:bg-zinc-500'
                        }`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                          </svg>
                        </div>
                        <span className="font-medium">{item.name}</span>
                      </div>
                      {item.count && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          activeSection === item.id 
                            ? 'bg-black text-white' 
                            : 'bg-zinc-600 text-gray-300'
                        }`}>
                          {item.count}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
                
                {/* Email Management Section */}
                <div className="pt-4">
                  <p className="text-xs uppercase text-gray-500 font-semibold mb-3 px-2">Email Management</p>
                  
                  {[
                    { 
                      id: 'email-campaign', 
                      name: 'Campaigns', 
                      icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                      gradient: 'from-green-500 to-green-600',
                      count: Math.floor(dashboardStats.emailsSent / 1000) + 'K'
                    }
                  ].map((item) => (
                    <motion.div
                      key={item.id}
                      onClick={() => handleSectionChange(item.id)}
                      className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                        activeSection === item.id
                          ? 'bg-gradient-to-r from-white to-gray-100 text-black shadow-lg'
                          : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300 hover:text-white'
                      }`}
                      whileHover={{ x: 4, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                            activeSection === item.id 
                              ? `bg-gradient-to-r ${item.gradient} text-white` 
                              : 'bg-zinc-600 group-hover:bg-zinc-500'
                          }`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                            </svg>
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          activeSection === item.id 
                            ? 'bg-black text-white' 
                            : 'bg-zinc-600 text-gray-300'
                        }`}>
                          {item.count}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* System Section */}
                <div className="pt-4">
                  <p className="text-xs uppercase text-gray-500 font-semibold mb-3 px-2">System</p>
                  
                  {[
                    { 
                      id: 'settings', 
                      name: 'Settings', 
                      icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
                      gradient: 'from-purple-500 to-purple-600'
                    },
                    { 
                      id: 'analytics', 
                      name: 'Analytics', 
                      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                      gradient: 'from-orange-500 to-orange-600'
                    }
                  ].map((item) => (
                    <motion.div
                      key={item.id}
                      onClick={() => handleSectionChange(item.id)}
                      className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                        activeSection === item.id
                          ? 'bg-gradient-to-r from-white to-gray-100 text-black shadow-lg'
                          : 'bg-zinc-700 hover:bg-zinc-600 text-gray-300 hover:text-white'
                      }`}
                      whileHover={{ x: 4, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center p-4">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                          activeSection === item.id 
                            ? `bg-gradient-to-r ${item.gradient} text-white` 
                            : 'bg-zinc-600 group-hover:bg-zinc-500'
                        }`}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                          </svg>
                        </div>
                        <span className="font-medium">{item.name}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </nav>
            </motion.div>
          </div>
          
          {/* Enhanced Main Content */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              {activeSection === 'overview' && (
                <div>
                  {/* Header */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between">
                      <div>
                        <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
                        <p className="text-gray-400">Welcome back! Here's what's happening with your platform.</p>
                      </div>
                      
                      <motion.button
                        onClick={refreshDashboardStats}
                        disabled={statsLoading}
                        className="flex items-center space-x-2 bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-4 py-2 rounded-xl transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg className={`w-4 h-4 ${statsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>{statsLoading ? 'Loading...' : 'Refresh Data'}</span>
                      </motion.button>
                    </div>
                    
                    {/* Error Display */}
                    {statsError && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mt-4"
                      >
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <span>{statsError}</span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {[
                      {
                        title: 'Total Users',
                        value: statsLoading ? '...' : dashboardStats.totalUsers.toLocaleString(),
                        change: '+12.5%',
                        changeType: 'positive',
                        icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a4 4 0 11-8 0',
                        gradient: 'from-blue-500 to-blue-600'
                      },
                      {
                        title: 'Wishlist Entries',
                        value: statsLoading ? '...' : dashboardStats.wishlistEntries.toLocaleString(),
                        change: '+8.2%',
                        changeType: 'positive',
                        icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                        gradient: 'from-pink-500 to-pink-600'
                      },
                      {
                        title: 'Emails Sent',
                        value: statsLoading ? '...' : dashboardStats.emailsSent.toLocaleString(),
                        change: '+15.3%',
                        changeType: 'positive',
                        icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                        gradient: 'from-green-500 to-green-600'
                      },
                      {
                        title: 'Conversion Rate',
                        value: statsLoading ? '...' : (dashboardStats.conversionRate + '%'),
                        change: '+2.1%',
                        changeType: 'positive',
                        icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
                        gradient: 'from-purple-500 to-purple-600'
                      }
                    ].map((metric, index) => (
                      <motion.div
                        key={metric.title}
                        className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700 relative overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                      >
                        <div className="absolute top-0 right-0 w-20 h-20 -mr-10 -mt-10">
                          <div className={`w-full h-full bg-gradient-to-br ${metric.gradient} rounded-full opacity-10`}></div>
                        </div>
                        
                        <div className="relative">
                          <div className="flex items-center justify-between mb-3">
                            <div className={`w-12 h-12 bg-gradient-to-r ${metric.gradient} rounded-xl flex items-center justify-center`}>
                              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={metric.icon} />
                              </svg>
                            </div>
                            <span className={`text-sm px-2 py-1 rounded-full ${
                              metric.changeType === 'positive' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {metric.change}
                            </span>
                          </div>
                          
                          <h3 className="text-gray-400 text-sm font-medium mb-1">{metric.title}</h3>
                          <p className="text-3xl font-bold text-white">{metric.value}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {[
                      {
                        title: 'Wishlist Management',
                        description: 'View and manage user wishlist entries',
                        action: 'View Entries',
                        section: 'wishlist',
                        icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                        gradient: 'from-pink-500 to-pink-600',
                        bgGradient: 'from-pink-500/10 to-pink-600/10'
                      },
                      {
                        title: 'Email Campaigns',
                        description: 'Create and send email campaigns to subscribers',
                        action: 'Create Campaign',
                        section: 'email-campaign',
                        icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                        gradient: 'from-green-500 to-green-600',
                        bgGradient: 'from-green-500/10 to-green-600/10'
                      },
                      {
                        title: 'Analytics Dashboard',
                        description: 'View detailed analytics and performance metrics',
                        action: 'View Analytics',
                        section: 'analytics',
                        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                        gradient: 'from-orange-500 to-orange-600',
                        bgGradient: 'from-orange-500/10 to-orange-600/10'
                      }
                    ].map((card, index) => (
                      <motion.div
                        key={card.title}
                        className={`bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700 relative overflow-hidden cursor-pointer`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                        whileHover={{ y: -5, scale: 1.02 }}
                        onClick={() => handleSectionChange(card.section)}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient}`}></div>
                        
                        <div className="relative">
                          <div className={`w-14 h-14 bg-gradient-to-r ${card.gradient} rounded-2xl flex items-center justify-center mb-4`}>
                            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                            </svg>
                          </div>
                          
                          <h3 className="text-xl font-bold text-white mb-2">{card.title}</h3>
                          <p className="text-gray-400 mb-4 leading-relaxed">{card.description}</p>
                          
                          <motion.button
                            className={`bg-gradient-to-r ${card.gradient} hover:opacity-90 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center space-x-2`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span>{card.action}</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Recent Activity */}
                  <motion.div
                    className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-white">Recent Activity</h3>
                      <div className="flex items-center space-x-2 text-sm text-gray-400">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span>Live</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {activitiesLoading ? (
                        // Loading skeleton
                        Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className="flex items-center space-x-4 p-3 rounded-xl bg-zinc-700/50">
                            <div className="w-8 h-8 rounded-lg bg-zinc-600 animate-pulse"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-zinc-600 rounded animate-pulse w-3/4"></div>
                              <div className="h-3 bg-zinc-600 rounded animate-pulse w-1/2"></div>
                            </div>
                            <div className="h-3 bg-zinc-600 rounded animate-pulse w-16"></div>
                          </div>
                        ))
                      ) : recentActivities.length > 0 ? (
                        recentActivities.map((activity, index) => (
                        <motion.div
                          key={index}
                          className="flex items-center space-x-4 p-3 rounded-xl bg-zinc-700/50 hover:bg-zinc-700/70 transition-colors"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            activity.type === 'wishlist' ? 'bg-pink-500' :
                            activity.type === 'email' ? 'bg-green-500' :
                            activity.type === 'user' ? 'bg-blue-500' : 'bg-purple-500'
                          }`}>
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                                activity.type === 'wishlist' ? 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' :
                                activity.type === 'email' ? 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' :
                                activity.type === 'user' ? 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' :
                                'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                              } />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{activity.action}</p>
                            <p className="text-gray-400 text-sm">{activity.user || activity.details}</p>
                          </div>
                          <span className="text-gray-500 text-sm">{activity.timeAgo}</span>
                        </motion.div>
                        ))
                      ) : (
                        // No activities state
                        <div className="text-center py-8">
                          <div className="w-12 h-12 bg-zinc-700 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <p className="text-gray-400">No recent activities</p>
                          <p className="text-gray-500 text-sm mt-1">Activities will appear here as they happen</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                </div>
              )}
              
              {activeSection === 'wishlist' && (
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700">
                  <WishlistView />
                </div>
              )}
              
              {activeSection === 'email-campaign' && (
                <div className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 shadow-2xl border border-zinc-700">
                  <EmailCampaignView />
                </div>
              )}
              
              {activeSection === 'settings' && (
                <motion.div
                  className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-8 shadow-2xl border border-zinc-700"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">App Settings</h1>
                    <p className="text-gray-400 text-lg mb-6">Configure your application settings and preferences</p>
                    <div className="inline-flex items-center space-x-2 text-yellow-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span>Settings page under construction</span>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {activeSection === 'analytics' && (
                <motion.div
                  className="bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-8 shadow-2xl border border-zinc-700"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-4">Analytics Dashboard</h1>
                    <p className="text-gray-400 text-lg mb-6">Comprehensive analytics and performance insights</p>
                    <div className="inline-flex items-center space-x-2 text-yellow-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span>Analytics page under construction</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Security Monitor */}
      <SecurityMonitor isVisible={showSecurityMonitor} />
    </div>
  );
};

export default DashboardView; 